import type { AssetType, InvestmentStrategy } from '../api/generated/client';

export const RESOURCE_KEY_LENGTH = 80; // max length for topic.key and talkingPoint.key
export const CLIENT_SEARCH_MIN_LENGTH = 3; // search triggered if at least 3 characters entered

export const FONT_SIZE_SM = '10px';
export const FONT_SIZE_MD = '14px';
export const FONT_WEIGHT_REGULAR = 400;
export const FONT_WEIGHT_BOLD = 700;
export const FONT_FAMILY_VERLAG = 'Verlag SSm For Baer, sans-serif';

export const MONTH_12_VALUE = 11;

export const investmentStrategyMap: Record<InvestmentStrategy, string> = {
    INCOME: 'wealthInvestmentStrategyIncome',
    FIXED_INCOME: 'wealthInvestmentStrategyFixedIncome',
    GROWTH: 'wealthInvestmentStrategyGrowth',
    BALANCED: 'wealthInvestmentStrategyBalanced',
    CAPITAL_GAIN: 'wealthInvestmentStrategyCapitalGain',
};

export const ROADMAP_REMINDER_DAYS = 14; // if reminder date of life event or advisory need is within next 14 days, it will be shown in a notification badge

export const DEFAULT_INPUT_DATE_FORMAT = 'DD/MM/YYYY';
export const INCOME_EXPENSE_INPUT_DATE_FORMAT = 'MM/YYYY';

// Projection

export const SAE_SUCCESS_MESSAGE = 'Success.';

export const CASHFLOW_GROSS_INCOME_CLASS_NAME = 'cashflow-gross-income';
export const CASHFLOW_GROSS_EXPENSE_CLASS_NAME = 'cashflow-gross-expense';

export type ChartType = 'CASH_FLOW' | 'MONTE_CARLO_PROJECTION';
export type ProjectionPath = 'expected' | 'median' | 'optimistic' | 'conservative' | 'custom';

export const PAYLOAD_DATE_FORM = 'DD-MM-YYYY';
export const X_AXIS_OFFSET = 54;
export const Y_AXIS_OFFSET = 60;
export const CLIENT_OUTPUT_Y_AXIS_OFFSET = 75;
export const DEFAULT_TIME_RANGE = 15;
export const DEFAULT_CONFIDENCE_INTERVAL_UPPER_LEVEL = 95;
export const DEFAULT_CONFIDENCE_INTERVAL_LOWER_LEVEL = 5;
export const DEFAULT_INFLATION = 1.5;
export const DEFAULT_CHART_NAVIGATOR_RANGE = DEFAULT_TIME_RANGE * 365 * 24 * 60 * 60 * 1000; // 15 years
export const CHART_TYPE_COLUMN = 'column';
export const CHART_TYPE_LINE = 'line';
export const LIFE_EVENT_PANE = 'lifeEventPane';
export const LIFE_EVENT_TITLE = 'lifeEventTitle';
export const YEAR_LABEL = 'yearLabel';
export const AGE_LABEL = 'ageLabel';
export const NAVIGATION_BAR_HEIGHT = 124;
export const PRIMARY_NAVIGATION_BAR_HEIGHT = 64;
export const SETTINGS_DRAWER_WIDTH = 570;
export const SETTINGS_DRAWER_CONTENT_WIDTH = 515;
export const SESSIONS_PANEL_WIDTH = 400;
export const PANEL_PADDING = 24;
export const CLIENT_OUTPUT_AT_PROJECTION_START_TEXT = 'atProjectionStartText';
export const CLIENT_OUTPUT_AT_PROJECTION_START_VALUE_TEXT = 'atProjectionStartValueText';
export const CLIENT_OUTPUT_AT_PROJECTION_END_TEXT = 'atProjectionEndText';
export const CLIENT_OUTPUT_STARTING_DOT_CIRCLE = 'startingDotCircle';

export const CLIENT_OUTPUT_EVENT_YEAR_0_ID = 'client-output-events-page-0';
export const CLIENT_OUTPUT_EVENT_YEAR_1_ID = 'client-output-events-page-1';
export const CLIENT_OUTPUT_EVENT_YEAR_2_ID = 'client-output-events-page-2';

export const WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID = 1;
export const WEALTH_ASSET_BANKABLE_CATEGORY_ID = 2;
export const WEALTH_ASSET_PENSION_CATEGORY_ID = 3;
export const WEALTH_ASSET_INSURANCE_CATEGORY_ID = 4;
export const WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID = 5;
export const WEALTH_ASSET_OTHER_CATEGORY_ID = 6;

export const WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID = 7;
export const WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID = 8;
export const WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID = 9;
export const WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID = 10;
export const WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID = 23;
export const WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID = 11;

export const WEALTH_EXPENSE_PREMIUMS = 22;
export const WEALTH_EXPENSE_REAL_ESTATE = 16;
export const WEALTH_EXPENSE_GENERAL_LIVING = 15;
export const WEALTH_EXPENSE_INTEREST_BURDEN = 21;
export const WEALTH_EXPENSE_OTHER = 17;

export const WEALTH_INCOME_EMPLOYMENT = 12;
export const WEALTH_INCOME_OTHER = 20;
export const WEALTH_INCOME_SECURITIES = 13;
export const WEALTH_INCOME_REAL_ESTATE = 18;
export const WEALTH_INCOME_PENSION = 19;
export const WEALTH_INCOME_INSURANCE = 14;
export const WEALTH_INCOME_SHAREHOLDING = 25;

export const WRITABLE_CATEGORIES_CLM = [
    // assets
    WEALTH_ASSET_BANKABLE_CATEGORY_ID,
    WEALTH_ASSET_PENSION_CATEGORY_ID,
    WEALTH_ASSET_INSURANCE_CATEGORY_ID,
    WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID,
    WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID,
    WEALTH_ASSET_OTHER_CATEGORY_ID,
    // liabilities
    WEALTH_LIABILITY_MORTGAGE_CATEGORY_ID,
    WEALTH_LIABILITY_LOMBARD_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_UNSECURED_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_PRIVATE_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_POLICY_LOAN_CATEGORY_ID,
    WEALTH_LIABILITY_OTHER_LIABILITY_CATEGORY_ID,
    // expenses
    WEALTH_EXPENSE_GENERAL_LIVING,
    WEALTH_EXPENSE_INTEREST_BURDEN,
    WEALTH_EXPENSE_REAL_ESTATE,
    WEALTH_EXPENSE_PREMIUMS,
    WEALTH_EXPENSE_OTHER,
    // income
    WEALTH_INCOME_EMPLOYMENT,
    WEALTH_INCOME_OTHER,
    WEALTH_INCOME_SECURITIES,
    WEALTH_INCOME_REAL_ESTATE,
    WEALTH_INCOME_PENSION,
    WEALTH_INCOME_INSURANCE,
    WEALTH_INCOME_SHAREHOLDING,
];

export const wealthOverviewCategories: Map<AssetType, number> = new Map([
    ['BANKABLE_ASSET', WEALTH_ASSET_BANKABLE_CATEGORY_ID],
    ['PENSION', WEALTH_ASSET_PENSION_CATEGORY_ID],
    ['INSURANCE', WEALTH_ASSET_INSURANCE_CATEGORY_ID],
    ['REAL_ESTATE', WEALTH_ASSET_REAL_ESTATE_CATEGORY_ID],
    ['SHAREHOLDINGS', WEALTH_ASSET_SHAREHOLDING_CATEGORY_ID],
    ['OTHER_ASSET', WEALTH_ASSET_OTHER_CATEGORY_ID],
]);

export const PORTFOLIO_NUMBER_REGEX_CH = `^[0-9]{4}[.]?[0-9]{4}[/]?[0-9]{4}$`;

// client package document
export const CLIENT_PACKAGE_DOCUMENT_TYPE = 'application/pdf';

// Links used in Cross-border warning
export const CROSS_BORDER_COUNTRY_GUIDELINE_URL = 'https://juliusbaer.navexone.eu/content/docview/?app=pt&source=unspecified&docid=20706';
export const CROSS_BORDER_COUNTRY_COVERAGE_CENTRE_URL = 'https://apps3.juliusbaer.com/sites/spnmarth22/Pages/default.aspx';

export const chartColors = {
    GRID_LINE: '#d5d6e0',
    GRID_LINE_CLIENT_OUTPUT: '#d5d6e0',
    LABEL: '#595a5e',
    LABEL_CLIENT_OUTPUT: '#333333',
    CROSSHAIR: '#595a5e',
    TITLE: '#ffffff',
    EVENT_PANE_BACKGROUND: '#edeff7',
    EVENT_PANE_BACKGROUND_CLIENT_OUTPUT: '#edeff7',
};
